/**
 * "Reads as": a flow as one sentence a person who never saw the editor understands —
 * "When harsh brake is detected with value at least 0.4 g, on a vehicle in North, request 20 s of
 * forward video, then create a task “Check the footage” for someone who can edit vehicles."
 *
 * The sentence is built from parts so the page can highlight values and link each part to the
 * section that sets it. Every phrase goes through t() with placeholders, so NL and FR read
 * naturally; catalogue labels the server does not translate are shown as they come.
 */
import { formatDuration, formatNumber } from '@/lib/format';
import { msg, t } from '@/lib/i18n';
import { COMMON_FIELDS, DETECTION_UNIT, detectionLabel, OP_LABEL, optionLabel, SUBJECT, TRIGGER_PHRASE } from '../labels';
import { allSteps, type Condition, type FlowDef, refOf, type Step } from './definition';

export type PartKind = 'text' | 'trigger' | 'value' | 'action' | 'who' | 'target' | 'step' | 'muted';

export interface Part {
  text: string;
  kind: PartKind;
  /** What the part is about: 'trigger' | 'target' | 'conditions' | 'cooldown' | 'step:<id>'. */
  ref?: string;
}

export interface FieldInfo {
  key: string;
  label: string;
  type: string;
  options?: string[];
  unit?: string | null;
}

export interface TriggerInfo {
  key: string;
  label: string;
  subject: string;
  fields: FieldInfo[];
  /** False for the generic trigger every emitted event type has. */
  curated?: boolean;
}

export interface ActionInfo {
  key: string;
  label: string;
}

/** What the sentence needs to name things: the catalogue plus names of chosen ids. */
export interface SentenceContext {
  triggers: Map<string, TriggerInfo>;
  actions: Map<string, ActionInfo>;
  permissions: Map<string, string>;
  personas: Map<string, string>;
  events: Map<string, string>;
  /** id → name for groups, vehicles, drivers, assets, projects and people. */
  names: Map<string, string>;
}

export const emptyContext = (): SentenceContext => ({
  triggers: new Map(),
  actions: new Map(),
  permissions: new Map(),
  personas: new Map(),
  events: new Map(),
  names: new Map(),
});

const txt = (text: string): Part => ({ text, kind: 'text' });

/** Split a translated template at {placeholders} and put the given parts in their place. */
export function fill(template: string, vars: Record<string, Part[] | string>, kind: PartKind = 'value', ref?: string): Part[] {
  const out: Part[] = [];
  const re = /\{(\w+)\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template))) {
    if (m.index > last) out.push(txt(template.slice(last, m.index)));
    const v = vars[m[1] as string];
    if (v === undefined) out.push(txt(m[0]));
    else if (typeof v === 'string') out.push({ text: v, kind, ref });
    else out.push(...v);
    last = m.index + m[0].length;
  }
  if (last < template.length) out.push(txt(template.slice(last)));
  return out;
}

/** "a", "a or b", "a, b or c" (or "and"). */
export function joinParts(items: Part[][], word: 'or' | 'and'): Part[] {
  if (!items.length) return [];
  if (items.length === 1) return items[0] as Part[];
  const sep = word === 'or' ? t(' or ') : t(' and ');
  const out: Part[] = [];
  items.forEach((it, i) => {
    if (i > 0) out.push(txt(i === items.length - 1 ? sep : ', '));
    out.push(...it);
  });
  return out;
}

const lcFirst = (s: string): string => (s.length > 1 && s[1] === s[1]?.toUpperCase() && /[A-Z]/.test(s[1] ?? '') ? s : s.charAt(0).toLowerCase() + s.slice(1));
const plainLabel = (s: string): string => lcFirst(s.replace(/\s*\([^)]*\)\s*/g, ' ').trim());

export function fieldInfo(def: FlowDef, ctx: SentenceContext, key: string): FieldInfo {
  const trig = ctx.triggers.get(def.trigger.type);
  const f = trig?.fields.find((x) => x.key === key);
  if (f) return { ...f, label: t(f.label) };
  const common = COMMON_FIELDS[key];
  if (common)
    return {
      key,
      label: t(common),
      type: key.endsWith('_id') ? 'id' : key === 'severity' ? 'enum' : 'text',
      options: key === 'severity' ? ['info', 'low', 'medium', 'high'] : undefined,
    };
  if (key.startsWith('steps.')) {
    const [, id, , ...rest] = key.split('.');
    return { key, label: t('{field} of step {step}', { field: rest.join('.') || 'output', step: id ?? '' }), type: 'text' };
  }
  return { key, label: key.replace(/_/g, ' '), type: 'text' };
}

/** The unit of a number field: declared by the trigger, or implied by the detection type. */
function unitOf(def: FlowDef, f: FieldInfo): string | null {
  if (f.unit) return f.unit;
  if (def.trigger.type === 'telemetry.detection' && f.key === 'value') {
    const typeCond = def.trigger.where.find((c) => c.field === 'type');
    const types = Array.isArray(typeCond?.value) ? typeCond.value : [typeCond?.value];
    const units = new Set(types.map((x) => DETECTION_UNIT[String(x)]).filter(Boolean));
    return units.size === 1 ? ([...units][0] ?? null) : null;
  }
  return null;
}

export function valueText(def: FlowDef, ctx: SentenceContext, f: FieldInfo, v: unknown): string {
  if (Array.isArray(v)) {
    const items = v.map((x) => valueText(def, ctx, f, x));
    return items.length > 1 ? `${items.slice(0, -1).join(', ')}${t(' or ')}${items[items.length - 1]}` : (items[0] ?? '');
  }
  if (v === null || v === undefined || v === '') return '…';
  const ref = refOf(v);
  if (ref) return refText(def, ctx, ref);
  if (f.type === 'bool') return v === true || v === 'true' ? t('yes') : t('no');
  if (f.type === 'duration') return formatDuration(Number(v));
  if (f.type === 'number' || typeof v === 'number') {
    const n = Number(v);
    const unit = unitOf(def, f);
    return Number.isNaN(n) ? String(v) : unit ? `${formatNumber(n, 2)} ${unit}` : formatNumber(n, 2);
  }
  if (f.type === 'id' || f.key.endsWith('_id')) return ctx.names.get(String(v)) ?? String(v).slice(0, 8);
  if (f.type === 'enum' || f.options?.length) return optionLabel(v);
  return `“${String(v)}”`;
}

/** {{trigger.driver_id}} → "the event's driver" etc. */
export function refText(def: FlowDef, ctx: SentenceContext, ref: string): string {
  const [root, ...rest] = ref.split('.');
  const key = rest.join('.');
  if (root === 'trigger') return t('the event’s {field}', { field: plainLabel(fieldInfo(def, ctx, key).label) });
  if (root === 'subject') return t('the subject');
  if (root === 'steps') return t('the result of {step}', { step: stepName(def, rest[0]) });
  return ref;
}

/** Text with {{references}} for reading: "Acknowledge: [title]". */
export function humanText(def: FlowDef, ctx: SentenceContext, text: string): string {
  return text.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_, ref: string) => {
    const [root, ...rest] = ref.split('.');
    if (root === 'trigger') return `[${plainLabel(fieldInfo(def, ctx, rest.join('.')).label)}]`;
    return `[${refText(def, ctx, ref)}]`;
  });
}

/** Operator words after "with …" in a trigger ("with severity high", "with value over 0.4"). */
const WITH_OP: Record<Condition['op'], string | null> = {
  '=': null,
  '!=': msg('not'),
  '>': msg('over'),
  '>=': msg('at least'),
  '<': msg('under'),
  '<=': msg('at most'),
  in: null,
  not_in: msg('not'),
  contains: msg('containing'),
  exists: msg('known'),
};

/** Conditions on what earlier steps returned, said as what they mean. */
function stepOutputParts(def: FlowDef, c: Condition): Part[] | null {
  const m = /^steps\.(\w+)\.output\.(timed_out|decision|event)$/.exec(c.field);
  if (!m) return null;
  const step = stepName(def, m[1]);
  const yes = c.op === 'exists' || ((c.op === '=' || c.op === 'in') && (c.value === true || c.value === 'true'));
  const no = (c.op === '=' && (c.value === false || c.value === 'false')) || (c.op === '!=' && (c.value === true || c.value === 'true'));
  if (m[2] === 'timed_out' && (yes || no)) return [txt(yes ? t('{step} ran out of time', { step }) : t('{step} did not run out of time', { step }))];
  if (m[2] === 'event' && c.op === 'exists') return [txt(t('what {step} waited for happened', { step }))];
  if (m[2] === 'decision' && (yes || no))
    return [txt(yes ? t('the assistant answered yes in {step}', { step }) : t('the assistant answered no in {step}', { step }))];
  return null;
}

export function conditionParts(def: FlowDef, ctx: SentenceContext, c: Condition, ref: string, style: 'is' | 'with' = 'is'): Part[] {
  const special = stepOutputParts(def, c);
  if (special) return special;
  const f = fieldInfo(def, ctx, c.field);
  const field = { text: plainLabel(f.label), kind: 'text' as const };
  if (style === 'with') {
    const w = WITH_OP[c.op];
    const word = w ? t(w) : '';
    if (c.op === 'exists') return [field, txt(` ${word}`)];
    return [field, txt(word ? ` ${word} ` : ' '), { text: valueText(def, ctx, f, c.value), kind: 'value', ref }];
  }
  const op = t(OP_LABEL[c.op]);
  if (c.op === 'exists') return [field, txt(` ${op}`)];
  const value: Part = { text: valueText(def, ctx, f, c.value), kind: 'value', ref };
  // "is one of a or b" reads better as "is a or b"
  const opText = c.op === 'in' ? t(OP_LABEL['=']) : op;
  return [field, txt(` ${opText} `), value];
}

/** "of at least 0.4 g" — the amount of a driving event, said with the event. */
const AMOUNT_OP: Partial<Record<Condition['op'], string>> = {
  '=': msg('of {value}'),
  '>': msg('over {value}'),
  '>=': msg('of at least {value}'),
  '<': msg('under {value}'),
  '<=': msg('of at most {value}'),
};

function whenParts(def: FlowDef, ctx: SentenceContext): Part[] {
  const type = def.trigger.type;
  if (!type) return [{ text: t('When … (choose what starts it)'), kind: 'muted', ref: 'trigger' }];
  let where = def.trigger.where;
  let lead: Part[];
  if (type === 'telemetry.detection') {
    const typeCond = where.find((c) => c.field === 'type' && (c.op === '=' || c.op === 'in'));
    const amounts = where.filter((c) => c.field === 'value' && AMOUNT_OP[c.op]);
    where = where.filter((c) => c !== typeCond && !amounts.includes(c));
    const values = typeCond ? (Array.isArray(typeCond.value) ? typeCond.value : [typeCond.value]) : [];
    const f = fieldInfo(def, ctx, 'value');
    const amount = amounts.flatMap((c) => [
      txt(' '),
      ...fill(t(AMOUNT_OP[c.op] ?? '{value}'), { value: [{ text: valueText(def, ctx, f, c.value), kind: 'value', ref: 'trigger' }] }),
    ]);
    const what = values.length
      ? joinParts(
          values.map((v) => [{ text: detectionLabel(v), kind: 'trigger', ref: 'trigger' }]),
          'or',
        )
      : null;
    lead = what
      ? fill(t('When {event} is detected'), { event: [...what, ...amount] })
      : fill(t('When {event}'), { event: [{ text: t(TRIGGER_PHRASE[type] ?? ''), kind: 'trigger', ref: 'trigger' }, ...amount] });
  } else if (type === 'flows.schedule') {
    lead = [{ text: t('On a schedule'), kind: 'trigger', ref: 'trigger' }];
  } else if (type === 'flows.manual') {
    lead = [{ text: t('When started by hand'), kind: 'trigger', ref: 'trigger' }];
  } else if (TRIGGER_PHRASE[type] || ctx.triggers.get(type)?.curated) {
    const phrase = TRIGGER_PHRASE[type] ? t(TRIGGER_PHRASE[type]) : lcFirst(ctx.triggers.get(type)?.label ?? type);
    lead = fill(t('When {event}'), { event: [{ text: phrase, kind: 'trigger', ref: 'trigger' }] });
  } else {
    // generic trigger of an event type: "When “Vehicle added” happens"
    lead = fill(t('When “{event}” happens'), { event: [{ text: ctx.triggers.get(type)?.label ?? type, kind: 'trigger', ref: 'trigger' }] });
  }
  if (!where.length) return lead;
  return [
    ...lead,
    ...fill(t(' with {conditions}'), {
      conditions: joinParts(
        where.map((c) => conditionParts(def, ctx, c, 'trigger', 'with')),
        'and',
      ),
    }),
  ];
}

function namesOf(ctx: SentenceContext, ids: string[], ref: string): Part[] {
  const shown = ids.slice(0, 3).map((id) => [{ text: ctx.names.get(id) ?? t('one not found'), kind: 'target', ref } as Part]);
  if (ids.length > 3) shown.push([{ text: t('{count} more', { count: ids.length - 3 }), kind: 'target', ref }]);
  return joinParts(shown, 'or');
}

function targetParts(def: FlowDef, ctx: SentenceContext): Part[] {
  const tg = def.target;
  const subject = ctx.triggers.get(def.trigger.type)?.subject ?? 'none';
  const word = t(SUBJECT[subject] ?? SUBJECT.none ?? 'subject');
  const out: Part[] = [];
  if (tg.scope !== 'all') {
    const names = tg.ids.length ? namesOf(ctx, tg.ids, 'target') : [{ text: t('… (choose)'), kind: 'muted' as const, ref: 'target' }];
    if (tg.scope === 'groups') out.push(...fill(t('on a {subject} in {groups}'), { subject: word, groups: names }, 'target', 'target'));
    else out.push(...fill(t('only for {names}'), { names }));
  }
  if (tg.project_ids.length) {
    if (out.length) out.push(txt(' '));
    out.push(...fill(t('in project {projects}'), { projects: namesOf(ctx, tg.project_ids, 'target') }));
  }
  return out;
}

/** How a message goes: " by email", " in the app". */
const CHANNEL_PHRASE: Record<string, string> = {
  inapp: msg('in the app'),
  email: msg('by email'),
  sms: msg('by SMS'),
  whatsapp: msg('on WhatsApp'),
  push: msg('as a push notification'),
};

function channelParts(channel: unknown, ref: string): Part[] {
  const phrase = CHANNEL_PHRASE[String(channel)];
  return phrase ? [txt(' '), { text: t(phrase), kind: 'value', ref }] : [txt(` (${String(channel)})`)];
}

function recipientText(ctx: SentenceContext, to: string): string {
  const [kind, ...rest] = to.split(':');
  const id = rest.join(':');
  if (kind === 'role') return t('the {role}', { role: lcFirst(t(ctx.personas.get(id) ?? id.replace(/_/g, ' '))) });
  if (kind === 'driver') return refOf(id) ? t('the driver') : (ctx.names.get(id) ?? t('a driver'));
  if (kind === 'user') return refOf(id) ? t('the person concerned') : (ctx.names.get(id) ?? t('a colleague'));
  if (kind === 'email') return id;
  return to;
}

function whoParts(step: Step, ctx: SentenceContext, ref: string): Part[] {
  if (step.assignee?.startsWith('user:')) {
    const id = step.assignee.slice(5);
    return [{ text: ctx.names.get(id) ?? t('a chosen person'), kind: 'who', ref }];
  }
  if (step.permission) {
    return fill(t('someone who can “{permission}”'), { permission: ctx.permissions.get(step.permission) ?? step.permission }, 'who', ref);
  }
  return [{ text: t('anyone who works on tasks'), kind: 'who', ref }];
}

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : null;

function actionParts(step: Step, ctx: SentenceContext, def: FlowDef, ref: string): Part[] {
  const key = step.action;
  const w = step.with ?? {};
  const v = (s: string): Part => ({ text: s, kind: 'value', ref });
  if (!key) return [{ text: t('… (choose an action)'), kind: 'muted', ref }];
  switch (key) {
    case 'media.request_video': {
      const before = num(w.seconds_before);
      const after = num(w.seconds_after);
      const secs = before !== null || after !== null ? (before ?? 0) + (after ?? 0) : num(w.duration_s);
      const cams = Array.isArray(w.channels) ? w.channels.map((c) => [v(optionLabel(c))]) : [];
      const parts = secs
        ? fill(t('request {length} of {cameras} video'), {
            length: [v(formatDuration(secs))],
            cameras: cams.length ? joinParts(cams, 'and') : [v(t('camera'))],
          })
        : cams.length
          ? fill(t('request {cameras} video'), { cameras: joinParts(cams, 'and') })
          : [txt(t('request video'))];
      return w.wait_for_footage === true ? [...parts, txt(t(' and wait for the footage'))] : parts;
    }
    case 'comms.notify': {
      const to = Array.isArray(w.to) ? w.to.map(String) : typeof w.to === 'string' && w.to ? [w.to] : [];
      const who = to.length
        ? joinParts(
            to.map((x) => [{ text: recipientText(ctx, x), kind: 'who', ref } as Part]),
            'and',
          )
        : [{ text: t('… (choose who)'), kind: 'muted', ref } as Part];
      const parts = fill(t('notify {who}'), { who });
      return w.channel ? [...parts, ...channelParts(w.channel, ref)] : parts;
    }
    case 'comms.send_message': {
      const who = w.driver_id ? t('the driver') : w.user_id ? t('a colleague') : w.conversation_id ? t('the conversation') : t('someone');
      const parts = fill(t('send {who} a message'), { who: [{ text: who, kind: 'who', ref }] });
      return w.channel ? [...parts, ...channelParts(w.channel, ref)] : parts;
    }
    case 'coaching.open_case':
      return fill(t('open a coaching case for {who}'), {
        who: [{ text: refOf(w.driver_id) ? t('the driver') : (ctx.names.get(String(w.driver_id)) ?? t('the driver')), kind: 'who', ref }],
      });
    case 'coaching.plan_session':
      return [txt(w.send_invitation === true ? t('plan a coaching session and invite the driver') : t('plan a coaching session'))];
    case 'tasks.create':
      return fill(t('create a task “{title}”'), { title: [v(humanText(def, ctx, String(w.title ?? '…')))] });
    case 'integrations.send_webhook':
      return [txt(t('send the event to another system'))];
    default: {
      const label = ctx.actions.get(key)?.label ?? key;
      return fill(t('run “{action}”'), { action: [{ text: label, kind: 'action', ref }] });
    }
  }
}

export function stepName(def: FlowDef, id: string | null | undefined): string {
  if (!id) return t('carry on');
  const steps = allSteps(def);
  const i = steps.findIndex((s) => s.id === id);
  if (i < 0) return t('a missing step');
  const s = steps[i] as Step;
  return s.label ? t('step {n} “{label}”', { n: i + 1, label: s.label }) : t('step {n}', { n: i + 1 });
}

/** One step as a phrase ("request 20 s of forward video"). */
export function stepParts(def: FlowDef, ctx: SentenceContext, step: Step): Part[] {
  const ref = `step:${step.id}`;
  const v = (s: string): Part => ({ text: s, kind: 'value', ref });
  switch (step.type) {
    case 'action':
      return actionParts(step, ctx, def, ref);
    case 'wait': {
      const event = step.event ? (ctx.events.get(step.event) ?? step.event) : null;
      const parts = event ? fill(t('wait for “{event}”'), { event: [v(event)] }) : [{ text: t('wait for … (choose an event)'), kind: 'muted' as const, ref }];
      return step.timeout_s ? [...parts, ...fill(t(' (at most {time})'), { time: [v(formatDuration(step.timeout_s))] })] : parts;
    }
    case 'delay':
      return fill(t('wait {time}'), { time: [v(step.seconds ? formatDuration(step.seconds) : '…')] });
    case 'approval':
      return fill(t('ask {who} to approve “{title}”'), { who: whoParts(step, ctx, ref), title: [v(step.title ? humanText(def, ctx, step.title) : '…')] });
    case 'task': {
      const parts = fill(t('create a task “{title}” for {who}'), {
        title: [v(step.title ? humanText(def, ctx, step.title) : '…')],
        who: whoParts(step, ctx, ref),
      });
      return step.due_s ? [...parts, ...fill(t(', due within {time}'), { time: [v(formatDuration(step.due_s))] })] : parts;
    }
    case 'ai':
      return [txt(step.output === 'decision' ? t('let the assistant decide yes or no') : t('let the assistant write a text'))];
    case 'branch': {
      const conds = step.if?.length
        ? joinParts(
            step.if.map((c) => conditionParts(def, ctx, c, ref)),
            'and',
          )
        : [{ text: '…', kind: 'muted' as const, ref }];
      return fill(t('if {conditions}, go to {then}, otherwise {else}'), {
        conditions: conds,
        then: [{ text: stepName(def, step.then), kind: 'step', ref }],
        else: [{ text: step.else ? stepName(def, step.else) : t('carry on'), kind: 'step', ref }],
      });
    }
  }
}

export interface Sentence {
  when: Part[];
  target: Part[];
  only: Part[];
  /** Steps in order; `phase` names the phase a step opens (case flows). */
  steps: { id: string; phase?: string; parts: Part[] }[];
  limit: Part[];
}

export function describeFlow(def: FlowDef, ctx: SentenceContext): Sentence {
  const subject = ctx.triggers.get(def.trigger.type)?.subject ?? 'none';
  const steps: Sentence['steps'] = [];
  if (def.phases.length) {
    for (const p of def.phases)
      p.steps.forEach((s, i) => steps.push({ id: s.id, phase: i === 0 ? p.label || p.id : undefined, parts: stepParts(def, ctx, s) }));
  } else {
    for (const s of def.steps) steps.push({ id: s.id, parts: stepParts(def, ctx, s) });
  }
  const cd = def.cooldown;
  const limit =
    cd.seconds > 0
      ? cd.per === 'flow'
        ? fill(t('At most once every {time} for the whole automation.'), { time: formatDuration(cd.seconds) }, 'value', 'cooldown')
        : fill(
            t('At most once every {time} per {subject}.'),
            { time: formatDuration(cd.seconds), subject: t(SUBJECT[subject] ?? SUBJECT.none ?? '') },
            'value',
            'cooldown',
          )
      : [];
  return {
    when: whenParts(def, ctx),
    target: targetParts(def, ctx),
    only: def.conditions.length
      ? fill(t('but only if {conditions}'), {
          conditions: joinParts(
            def.conditions.map((c) => conditionParts(def, ctx, c, 'conditions')),
            'and',
          ),
        })
      : [],
    steps,
    limit,
  };
}

/** The whole sentence as plain text (for lists, the assistant and tests). */
export function sentenceText(s: Sentence): string {
  const j = (ps: Part[]) => ps.map((p) => p.text).join('');
  const head = [j(s.when), j(s.target), j(s.only)].filter(Boolean).join(', ');
  const body = s.steps.length ? s.steps.map((x) => j(x.parts)).join(t(', then ')) : t('… (add a step)');
  return `${head}, ${body}.${s.limit.length ? ` ${j(s.limit)}` : ''}`;
}
